import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import {
  createPwaProject,
  deletePwaProject,
  generatePwaDraft,
  getPwaProjects,
  updatePwaProject,
  type GeneratedPwaDraft,
  type PwaProject,
  type PwaGenerateMode,
} from '@/api/pwa';
import { COUNTRIES } from '@/lib/countries';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUseDraft: (draft: GeneratedPwaDraft, selection: PwaGeneratorSelection) => void;
}

export interface PwaGeneratorSelection {
  projectId: string;
  platform: 'ios' | 'android';
  geo: string;
}

const EMPTY_DRAFT: GeneratedPwaDraft = {
  app_name: '', developer_name: '', category: 'Tools', rating: 4.7,
  reviews_count: 1000, downloads_count: '100K+', size: '18 MB', version: '1.0.0',
  short_description: '', full_description: '', icon_url: '', splash_url: '',
  bg_color: '#2563EB', language_code: '', language_name: '', visual_direction: '', reviews: [],
  screenshots: [], offer_url: '', store_layout: 'play',
};

function Sparkles({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 3l1.3 4.1L17 9l-3.7 1.9L12 15l-1.3-4.1L7 9l3.7-1.9L12 3zM5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14zM19 13l.8 2.2L22 16l-2.2.8L19 19l-.8-2.2L16 16l2.2-.8L19 13z" />
    </svg>
  );
}

export function PwaGeneratorModal({ isOpen, onClose, onUseDraft }: Props) {
  const queryClient = useQueryClient();
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['pwa-projects'], queryFn: getPwaProjects, enabled: isOpen,
  });
  const [projectId, setProjectId] = useState('');
  const [platform, setPlatform] = useState<'ios' | 'android'>('android');
  const [geo, setGeo] = useState('');
  const [draft, setDraft] = useState<GeneratedPwaDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [projectEditorOpen, setProjectEditorOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectPrompt, setProjectPrompt] = useState('');
  const [projectSaving, setProjectSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setProjectId(''); setGeo(''); setPlatform('android'); setDraft(null); setError(''); setBusy(null);
    setProjectEditorOpen(false); setEditingProjectId(null); setProjectName(''); setProjectPrompt('');
  }, [isOpen]);

  const selectedCountry = useMemo(() => COUNTRIES.find((item) => item.code === geo), [geo]);
  const canGenerate = Boolean(projectId && geo && !busy);

  const openProjectEditor = (project?: PwaProject) => {
    setEditingProjectId(project?.id || null);
    setProjectName(project?.name || '');
    setProjectPrompt(project?.prompt || '');
    setProjectEditorOpen(true);
    setError('');
  };

  const saveProject = async () => {
    if (!projectName.trim() || !projectPrompt.trim()) {
      setError('Project name and prompt are required');
      return;
    }
    setProjectSaving(true);
    setError('');
    try {
      const saved = editingProjectId
        ? await updatePwaProject(editingProjectId, { name: projectName.trim(), prompt: projectPrompt.trim() })
        : await createPwaProject({ name: projectName.trim(), prompt: projectPrompt.trim() });
      await queryClient.invalidateQueries({ queryKey: ['pwa-projects'] });
      setProjectId(saved.id);
      setProjectEditorOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to save project');
    } finally {
      setProjectSaving(false);
    }
  };

  const removeProject = async () => {
    if (!editingProjectId) return;
    setProjectSaving(true);
    setError('');
    try {
      await deletePwaProject(editingProjectId);
      await queryClient.invalidateQueries({ queryKey: ['pwa-projects'] });
      if (projectId === editingProjectId) setProjectId('');
      setProjectEditorOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Failed to delete project');
    } finally {
      setProjectSaving(false);
    }
  };

  const runGeneration = async (mode: PwaGenerateMode, screenshotIndex?: number) => {
    if (!projectId || !geo) return;
    setBusy(mode === 'screenshot' ? `screenshot-${screenshotIndex}` : mode);
    setError('');
    try {
      const update = await generatePwaDraft({
        project_id: projectId, platform, geo, mode, screenshot_index: screenshotIndex,
        draft: draft || undefined,
      });
      setDraft((current) => {
        const next = { ...(current || EMPTY_DRAFT), ...update } as GeneratedPwaDraft;
        if (mode === 'screenshot' && typeof update.screenshot_index === 'number' && update.screenshot_url) {
          const screenshots = [...(current?.screenshots || [])];
          screenshots[update.screenshot_index] = update.screenshot_url;
          next.screenshots = screenshots.filter(Boolean);
        }
        return next;
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Generation failed');
    } finally {
      setBusy(null);
    }
  };

  const rerollButton = (mode: PwaGenerateMode, label: string, index?: number) => (
    <button
      type="button"
      disabled={Boolean(busy)}
      onClick={() => runGeneration(mode, index)}
      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-hover disabled:opacity-40"
    >
      <Sparkles className={`w-3.5 h-3.5 ${busy === (mode === 'screenshot' ? `screenshot-${index}` : mode) ? 'animate-pulse' : ''}`} />
      {busy === (mode === 'screenshot' ? `screenshot-${index}` : mode) ? 'Generating…' : label}
    </button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI PWA Generator" size="full">
      {!draft ? (
        <div className="min-h-full flex items-center justify-center py-6">
          <div className="w-full max-w-3xl">
            <div className="text-center mb-8">
              <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="w-6 h-6" /></div>
              <h3 className="text-2xl font-semibold text-content">Generate a ready-to-use PWA</h3>
              <p className="mt-2 text-sm text-content-subtle">The project brief defines the concept. Platform and GEO adapt the store layout, language and visuals.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 p-5 bg-surface-muted/50 border border-border rounded-2xl">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-content-muted">1. PWA project</label>
                  <div className="flex items-center gap-2">
                    {projectId && <button type="button" onClick={() => openProjectEditor(projects.find((item) => item.id === projectId))} className="text-[11px] font-medium text-content-subtle hover:text-primary">Edit</button>}
                    <button type="button" onClick={() => openProjectEditor()} className="text-[11px] font-medium text-primary hover:text-primary-hover">+ New</button>
                  </div>
                </div>
                <Select value={projectId} onChange={setProjectId} searchable placeholder={projectsLoading ? 'Loading…' : 'Select project'} options={projects.map((item) => ({ value: item.id, label: item.name }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-content-muted mb-2">2. Platform</label>
                <div className="grid grid-cols-2 p-1 rounded-lg border border-border bg-surface">
                  {(['android', 'ios'] as const).map((item) => <button key={item} type="button" onClick={() => setPlatform(item)} className={`py-1.5 rounded-md text-sm font-medium transition-colors ${platform === item ? 'bg-primary text-white shadow-sm' : 'text-content-subtle hover:text-content'}`}>{item === 'ios' ? 'iOS' : 'Android'}</button>)}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-content-muted mb-2">3. GEO</label>
                <Select value={geo} onChange={setGeo} searchable placeholder="Select country" options={COUNTRIES.map((item) => ({ value: item.code, label: `${item.flag} ${item.name}` }))} />
              </div>
            </div>
            {projectEditorOpen && (
              <div className="mt-4 p-5 border border-primary/20 bg-primary/5 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-semibold text-content">{editingProjectId ? 'Edit PWA project' : 'New PWA project'}</h4>
                    <p className="text-xs text-content-subtle mt-0.5">This prompt defines the product concept, copy direction and visual style.</p>
                  </div>
                  <button type="button" onClick={() => setProjectEditorOpen(false)} className="text-content-subtle hover:text-content">×</button>
                </div>
                <input value={projectName} onChange={(event) => setProjectName(event.target.value)} placeholder="Project name, e.g. Rate2Cash" className="w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm" />
                <textarea value={projectPrompt} onChange={(event) => setProjectPrompt(event.target.value)} rows={5} placeholder="Describe the product, audience, positioning, visual style, required screens and restrictions…" className="mt-3 w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm resize-y" />
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>{editingProjectId && <Button type="button" variant="danger" size="sm" disabled={projectSaving} onClick={removeProject}>Delete</Button>}</div>
                  <div className="flex gap-2"><Button type="button" variant="secondary" size="sm" onClick={() => setProjectEditorOpen(false)}>Cancel</Button><Button type="button" size="sm" loading={projectSaving} onClick={saveProject}>Save project</Button></div>
                </div>
              </div>
            )}
            {error && <p className="mt-4 text-center text-sm text-danger">{error}</p>}
            <div className="mt-6 flex justify-center"><Button size="lg" disabled={!canGenerate} loading={busy === 'all'} onClick={() => runGeneration('all')}><Sparkles className="w-4 h-4 mr-2" />Generate PWA</Button></div>
            <p className="mt-3 text-center text-xs text-content-subtle">Usually takes 1–2 minutes: copy, logo, splash and 4 phone screenshots are generated together.</p>
          </div>
        </div>
      ) : (
        <div className="h-full flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border">
            <div>
              <p className="text-sm font-semibold text-content">{selectedCountry?.flag} {selectedCountry?.name} · {platform === 'ios' ? 'iOS' : 'Android'}</p>
              <p className="text-xs text-content-subtle">Edit any text directly or regenerate only the part you do not like.</p>
            </div>
            <div className="flex items-center gap-3">{rerollButton('all', 'Regenerate all')}<Button onClick={() => onUseDraft(draft, { projectId, platform, geo })}>Continue in editor</Button></div>
          </div>
          {error && <div className="mt-3 p-2.5 rounded-lg bg-danger-soft text-sm text-danger">{error}</div>}
          <div className="grid lg:grid-cols-[minmax(320px,1fr)_390px] gap-8 pt-5 flex-1 min-h-0 overflow-y-auto">
            <div className="space-y-5">
              <section className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3"><div><h4 className="text-sm font-semibold text-content">Store copy</h4>{draft.language_name && <p className="text-xs text-content-subtle mt-0.5">{draft.language_name} · {draft.language_code}</p>}</div>{rerollButton('copy', 'Regenerate copy')}</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input value={draft.app_name} onChange={(e) => setDraft({ ...draft, app_name: e.target.value })} className="px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm" placeholder="App name" />
                  <input value={draft.developer_name} onChange={(e) => setDraft({ ...draft, developer_name: e.target.value })} className="px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm" placeholder="Developer" />
                </div>
                <input value={draft.short_description} onChange={(e) => setDraft({ ...draft, short_description: e.target.value })} className="mt-3 w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm" placeholder="Short description" />
                <textarea value={draft.full_description} onChange={(e) => setDraft({ ...draft, full_description: e.target.value })} rows={6} className="mt-3 w-full px-3 py-2 rounded-lg border border-border-strong bg-surface text-sm resize-y" placeholder="Full description" />
              </section>
              <section className="border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3"><h4 className="text-sm font-semibold text-content">Generated reviews</h4><span className="text-xs text-content-subtle">{draft.reviews.length} reviews · {draft.language_name}</span></div>
                <div className="space-y-3">
                  {draft.reviews.map((review) => <div key={review.id} className="rounded-lg bg-surface-muted p-3"><div className="flex items-center justify-between gap-3"><span className="text-xs font-semibold text-content">{review.name}</span><span className="text-xs text-warning">{'★'.repeat(review.rating)}</span></div><p className="mt-1.5 text-sm text-content-muted">{review.text}</p>{review.reply && <p className="mt-2 pl-3 border-l-2 border-primary/30 text-xs text-content-subtle">{review.reply}</p>}</div>)}
                  {draft.reviews.length === 0 && <p className="text-sm text-content-subtle">No reviews were generated. Regenerate the copy to try again.</p>}
                </div>
              </section>
              <section className="grid sm:grid-cols-2 gap-4">
                <AssetCard title="Logo" url={draft.icon_url} square action={rerollButton('icon', 'Regenerate logo')} />
                <AssetCard title="Splash" url={draft.splash_url} action={rerollButton('splash', 'Regenerate splash')} />
              </section>
              <section className="border border-border rounded-xl p-4">
                <h4 className="text-sm font-semibold text-content mb-3">Screenshots</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{draft.screenshots.map((url, index) => <AssetCard key={`${url}-${index}`} title={`Screen ${index + 1}`} url={url} compact action={rerollButton('screenshot', 'Regenerate', index)} />)}</div>
                {draft.screenshots.length === 0 && <p className="text-sm text-content-subtle">Images were not generated. Regenerate the whole draft to try again.</p>}
              </section>
            </div>
            <StorePreview draft={draft} platform={platform} />
          </div>
        </div>
      )}
    </Modal>
  );
}

function AssetCard({ title, url, action, square, compact }: { title: string; url: string; action: React.ReactNode; square?: boolean; compact?: boolean }) {
  return <div className={`border border-border rounded-xl p-3 ${compact ? '' : 'bg-surface'}`}><div className="flex items-center justify-between gap-2 mb-2"><span className="text-xs font-semibold text-content-muted">{title}</span>{action}</div><div className={`overflow-hidden rounded-lg bg-surface-muted ${square ? 'aspect-square max-h-48' : compact ? 'aspect-[9/16]' : 'aspect-[9/12] max-h-48'}`}>{url ? <img src={url} alt={title} className={`w-full h-full ${compact ? 'object-contain' : 'object-cover'}`} /> : <div className="w-full h-full grid place-items-center text-xs text-content-subtle">Not generated</div>}</div></div>;
}

function StorePreview({ draft, platform }: { draft: GeneratedPwaDraft; platform: 'ios' | 'android' }) {
  return (
    <div className="lg:sticky lg:top-0 self-start mx-auto w-full max-w-[390px]">
      <p className="mb-2 text-center text-xs font-medium text-content-subtle">LIVE PREVIEW</p>
      <div className="rounded-[2.25rem] border-[7px] border-content bg-surface shadow-xl overflow-hidden">
        <div className="h-7 px-5 flex items-center justify-between text-[10px] font-semibold"><span>9:41</span><span>● ◔ ▰</span></div>
        <div className="px-4 pb-6 max-h-[690px] overflow-y-auto">
          <div className="flex gap-3 pt-3">
            <div className={`w-20 h-20 shrink-0 overflow-hidden bg-surface-muted ${platform === 'ios' ? 'rounded-[18px]' : 'rounded-2xl'}`}>{draft.icon_url && <img src={draft.icon_url} className="w-full h-full object-cover" alt="" />}</div>
            <div className="min-w-0 flex-1"><h3 className="font-semibold text-content leading-tight truncate">{draft.app_name}</h3><p className="text-xs text-primary mt-1 truncate">{draft.developer_name}</p><p className="text-xs text-content-subtle mt-1">Contains ads · In-app purchases</p><button type="button" className={`mt-2 px-5 py-1.5 text-xs font-semibold text-white bg-primary ${platform === 'ios' ? 'rounded-full' : 'rounded-md'}`}>{platform === 'ios' ? 'GET' : 'Install'}</button></div>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border my-5 text-center"><Metric value={draft.rating.toFixed(1)} label="★ rating" /><Metric value={draft.downloads_count} label="downloads" /><Metric value={draft.size} label="size" /></div>
          <div className="flex gap-2 overflow-x-auto pb-1">{draft.screenshots.map((url, index) => <img key={index} src={url} alt="" className="w-[42%] shrink-0 aspect-[9/16] object-contain bg-surface-muted rounded-lg border border-border" />)}</div>
          <h4 className="font-semibold text-content mt-5">About this app</h4><p className="mt-2 text-sm text-content-muted whitespace-pre-line line-clamp-6">{draft.full_description || draft.short_description}</p>
          <div className="mt-4 inline-flex px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium">{draft.category}</div>
        </div>
      </div>
    </div>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return <div className="px-1"><p className="text-sm font-semibold text-content">{value}</p><p className="text-[10px] text-content-subtle">{label}</p></div>;
}
